/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import {camelCase, cloneDeep, filter, isEmpty, isUndefined, map, uniq} from 'lodash';
import moment from 'moment';

import { dayDifference, isBetweenFromAndToDates } from 'services/Dates';

import { filterFunctionMapping } from 'state/shared/FilterFunctionMappingUtil';

import { formatAccountNumber } from 'services/Formats';

import { extractAndFormatOwners } from 'services/Accounts';

import { RECEIVED_ACCOUNTS } from 'state/account/AccountBalancesActions';

import {
  DATE_OPTIONS,
  RECEIVED_HISTORICAL_TRANSACTIONS,
  RECEIVED_PENDING_TRANSACTIONS,
  SELECT_HISTORICAL_TRANSACTION_PREFIX,
  TIME_PERIOD,
  RECEIVED_PRIOR_BUSINESS_DATE,
  RECEIVED_HISTORICAL_TRANSACTIONS_V2,
  SELECT_HISTORICAL_TRANSACTION_PREFIX_V2,
  INCLUDED_ACCOUNTS, TRANSACTION_TYPE, UPDATE_HISTORICAL_TRANSACTION_ACCORDION_ARRAY, RECEIVED_PENDING_TRANSACTIONS_V2
} from './TransactionActions';

import { RECEIVED_CUSTOM_ACCOUNT_GROUPS } from 'state/preferences/PreferencesActions';

import { CUSTOM_ACCOUNT_GROUP_PREFIX } from 'state/account/SelectAccountsFilteringReducer';

import { concatAndSeparateArrays } from 'support/ArrayUtil';
import { ACTIVE_ADVISORY, isActiveAdvisory } from 'services/Products';
import {
  RAS_FEE,
  RETIREMENT_ADVISORY_FEE_LABEL,
  SHARE_CLASS_CONVERSION_LABEL,
  selectionIsShareClassConversion
} from 'services/Transactions';

import { showAccountLabel } from 'components/shared/Table/MobileTableSupport';
import {
  initialTransactionsHistoryFilteringStateV2
} from "../../components/layouts/Transaction/TransactionPageV2/TransactionPageUtils";
import {isBrokerageOrderStatusTablesEnabled} from "../../services/FeatureFlags";

export const initialTransactionsHistoryFilteringState = {
  dateOptions: {},
  timePeriod: {
    selection: '90',
    options: [
      {
        label: 'Past 10 Days',
        value: '10'
      },
      {
        label: 'Past 30 Days',
        value: '30'
      },
      {
        label: 'Past 60 Days',
        value: '60'
      },
      {
        label: 'Past 90 Days',
        value: '90'
      },
      {
        label: 'Past 180 Days',
        value: '180'
      },
      {
        label: 'Past 1 Year',
        value: '365'
      },
      {
        label: 'Past 2 Years',
        value: '730'
      },
      {
        label: 'All (Up to 3 Years)',
        value: '1095'
      }
    ]
  },
  includedAccounts: {
    selection: 'all',
    options: [
      {
        label: 'All Accounts',
        value: 'all'
      },
      {
        label: 'Mutual Fund',
        value: 'mutualFund'
      },
      {
        label: 'Brokerage',
        value: 'brokerage'
      },
      {
        label: 'ActivePlus Portfolios',
        value: 'activePlus'
      },
      {
        label: 'Retirement Advisory Service',
        value: 'activeAdvisory'
      },
      {
        label: 'Retirement',
        value: 'retirement'
      },
      {
        label: 'Non-Retirement',
        value: 'nonRetirement'
      },
      {
        label: 'Owned',
        value: 'owned'
      },
      {
        label: 'Related',
        value: 'related'
      },
      {
        label: 'View-Only',
        value: 'viewOnly'
      }
    ]
  },
  transactionType: {
    selection: 'all',
    options: [
      {
        label: 'All Transaction Types',
        value: 'all'
      }
    ]
  }
};

export const pendingTransactionsReducer = (state = [], action) => {
  const { type, transactions } = action;
  if (type === RECEIVED_PENDING_TRANSACTIONS && transactions) {
    return transactions.sort((a, b) =>
      moment.utc(b.date).diff(moment.utc(a.date))
    );
  }
  return state;
};

const buildTransactionsPendingTable = ({
  accounts = [],
  pendingTransactions
}) => {
  return pendingTransactions.map((transaction) => {
    let accountFromInfo,
      accountToInfo,
      toAccountWChecksum,
      { amount } = transaction;

    const {
      bankAccountNumber,
      confirmationNumber,
      costBasis,
      date,
      deliveryType,
      engagementName,
      fromAccountId,
      quantity,
      toAccountId,
      toAccountChecksum,
      toAccountNumber,
      toName,
      toTicker,
      type,
      typeDescriptor,
      unitCode,
      userCancellable,
      payeeDetail
    } = transaction;

    toAccountWChecksum = formatAccountNumber({
      accountChecksum: toAccountChecksum,
      accountNumber: toAccountNumber
    });

    const activeFromAccount = accounts.find(
      (account) => account.accountId === fromAccountId
    );
    const activeToAccount = accounts.find(
      (account) => account.accountId === toAccountId
    );

    if (activeFromAccount) {
      const {
        accountChecksum,
        accountName,
        accountNumber,
        accountType,
        productType,
        ticker,
        isViewOnly
      } = activeFromAccount;
      const accountWChecksum = formatAccountNumber({
        accountChecksum,
        accountNumber
      });

      accountFromInfo = {
        accountName,
        accountType,
        accountWChecksum,
        owners: extractAndFormatOwners([activeFromAccount]),
        productType,
        ticker,
        isViewOnly
      };
    }

    if (activeToAccount) {
      const {
        accountChecksum,
        accountName,
        accountNumber,
        accountType,
        productType,
        ticker,
        isViewOnly
      } = activeToAccount;

      const accountWChecksum = formatAccountNumber({
        accountChecksum,
        accountNumber
      });

      accountToInfo = {
        accountName,
        accountType,
        accountWChecksum,
        owners: extractAndFormatOwners([activeToAccount]),
        productType,
        ticker,
        isViewOnly
      };
    }

    return {
      amountInformation: {
        amount,
        quantity,
        unitCode
      },
      costBasis,
      fromInformation: {
        accountFromInfo,
        engagementName,
        type
      },
      bankAccountNumber,
      toInformation: {
        accountToInfo,
        deliveryType,
        toName,
        toTicker,
        toAccountWChecksum,
        type,
        payeeDetail
      },
      tradeDate: date,
      typeInformation: {
        confirmationNumber,
        typeDescriptor
      },
      userCancellable
    };
  });
};

function isWithinTimePeriod(transaction, selections) {
  if (isEmpty(selections[DATE_OPTIONS])) {
    return (
      dayDifference(transaction.date) >=
      -Math.abs(selections[TIME_PERIOD].selection)
    );
  }
  const { fromDate, toDate } = selections[DATE_OPTIONS].selection;
  return isBetweenFromAndToDates(transaction.date, fromDate, toDate);
}

export function isAccountIncluded(
  accounts = [],
  transaction,
  { options, selection },
  providers
) {
  const { accountId: transactionAccountId } = transaction;
  const customGroupAccountIds = (
    options.find(({ value }) => value === selection) || {}
  ).accountIds;
  const filterMapping = filterFunctionMapping(
    selection,
    providers,
    customGroupAccountIds
  );
  const filteredAccountIds = accounts.reduce((accum, current) => {
    if (filterMapping) {
      const filtered = filterMapping(current);
      if (filtered) {
        accum.push(current.accountId);
      }
    }
    return accum;
  }, []);
  return filteredAccountIds.some((id) => id === transactionAccountId);
}

function isTransactionForRasIncluded(accounts, transaction) {
  return (
    transaction.transactionTypeId === RAS_FEE &&
    accounts.filter(
      (account) =>
        account.accountId === transaction.accountId &&
        isActiveAdvisory(account.productType)
    ).length > 0
  );
}

export function isTransactionTypeIncluded(accounts, transaction, selection) {
  const { associatedWithShareClassConversion, category } = transaction;
  if (selection === RETIREMENT_ADVISORY_FEE_LABEL) {
    return isTransactionForRasIncluded(accounts, transaction);
  }
  if (category === selection ||
      selectionIsShareClassConversion(associatedWithShareClassConversion, selection)) {
    return !isTransactionForRasIncluded(accounts, transaction);
  }
  return selection === 'all';
}

const buildTransactionsHistoryTable = ({
  accounts,
  providers,
  rawTransactionsHistory,
  transactionsHistoryFilteringSelections
}) => {
  // create chain of transaction history filters
  const filteredTransactions = rawTransactionsHistory.reduce(
    (accum, current) => {
      if (
        isWithinTimePeriod(current, transactionsHistoryFilteringSelections) &&
        isAccountIncluded(
          accounts,
          current,
          transactionsHistoryFilteringSelections.includedAccounts,
          providers
        ) &&
        isTransactionTypeIncluded(
          accounts,
          current,
          transactionsHistoryFilteringSelections.transactionType.selection
        )
      ) {
        accum.push(current);
      }
      return accum;
    },
    []
  );

  return filteredTransactions.map((transaction) => {
    let accountInformation;
    const {
      accountId,
      amount,
      costBasis,
      date,
      detail: transactionDescription,
      price,
      quantity,
      ticker,
      type: transactionType,
      transactionTypeId,
      associatedWithShareClassConversion
    } = transaction;

    const activeAccount = accounts.find(
      (account) => account.accountId === accountId
    );

    if (activeAccount) {
      const {
        accountChecksum,
        accountName,
        accountNumber,
        accountType,
        productType,
        moneyMarket,
        isViewOnly
      } = activeAccount;

      const accountWChecksum = formatAccountNumber({
        accountChecksum,
        accountNumber
      });

      accountInformation = {
        accountName: `${accountName} - ${accountType}`,
        accountId,
        accountType,
        accountWChecksum,
        moneyMarket,
        associatedWithShareClassConversion,
        owners: extractAndFormatOwners(activeAccount),
        productType,
        ticker,
        isViewOnly,
        ...(showAccountLabel(activeAccount) && { accountLabel: accountId }),
      };
    }

    return {
      ...accountInformation,
      netDollarAmount: amount,
      price,
      quantity,
      tradeDate: date,
      costBasis,
      transactionDescription,
      transactionType,
      transactionTypeId
    };
  });
};

export const historicalTransactionsReducer = (state = [], action) => {
  const { transactions, type } = action;
  if (type === RECEIVED_HISTORICAL_TRANSACTIONS && transactions) {
    return transactions.sort((a, b) =>
      moment.utc(b.date).diff(moment.utc(a.date))
    );
  }
  return state;
};
historicalTransactionsReducer.priority = 50;

export const historicalTransactionsReducerV2 = (state = [], action) => {
  const { transactions, type } = action;
  if (type === RECEIVED_HISTORICAL_TRANSACTIONS_V2 && transactions) {
    return transactions;
  }
  return state;
};
historicalTransactionsReducerV2.priority = 50;

export const pendingTransactionsReducerV2 = (state = [], action) => {
  const { transactions, type } = action;
  if (type === RECEIVED_PENDING_TRANSACTIONS_V2 && transactions) {
    return transactions;
  }
  return state;
};
pendingTransactionsReducerV2.priority = 50;

export const selectTransactionsHistoryFilteringReducer = (
  state = cloneDeep(initialTransactionsHistoryFilteringState),
  action,
  {
    customerPreferences = {},
    accounts = [],
    products = {},
    rawTransactionsHistory = []
  }
) => {
  const { selection, type } = action;

  const isHistoricalTransactionFilterSelection = type.startsWith(
    SELECT_HISTORICAL_TRANSACTION_PREFIX
  );

  if (
    (type !== RECEIVED_HISTORICAL_TRANSACTIONS &&
    type !== RECEIVED_ACCOUNTS &&
    type !== RECEIVED_CUSTOM_ACCOUNT_GROUPS &&
    !isHistoricalTransactionFilterSelection) ||
    isBrokerageOrderStatusTablesEnabled()
  ) {
    return state;
  }

  const initialAccountFilterOptions =
    initialTransactionsHistoryFilteringState.includedAccounts.options;
  let transactionsHistoryFilteringSelections = cloneDeep(state);

  const nonPurgedAccounts = filter(accounts, { purgedAccount: false });
  if (
    !isEmpty(nonPurgedAccounts) &&
    !isEmpty(rawTransactionsHistory) &&
    !isHistoricalTransactionFilterSelection
  ) {
    const accountIdsLookup = {};
    rawTransactionsHistory.forEach(
      ({ accountId }) => (accountIdsLookup[accountId] = true)
    );

    const accountsInTransaction = nonPurgedAccounts.filter(
      ({ accountId }) => accountIdsLookup[accountId]
    );

    const [allAccountsOption, ...restAccountOptions] =
      initialAccountFilterOptions;

    const accountOptions = accountsInTransaction
      .map((account) => {
        const { accountId, accountName, accountType } = account;
        const accountWChecksum = formatAccountNumber(account);
        return {
          label: `${accountName} - ${accountWChecksum}${
            accountType ? ' - ' + accountType : ''
          }`,
          value: accountId
        };
      })
      .sort(({ label: x }, { label: y }) => x.localeCompare(y));

    const predefinedGroups = restAccountOptions.filter(({ value }) => {
      const filterMapping = filterFunctionMapping(
        value,
        products.providers || {}
      );
      return filterMapping && accountsInTransaction.some(filterMapping);
    });

    const customAccountGroupArray =
      type === RECEIVED_CUSTOM_ACCOUNT_GROUPS
        ? action.customAccountGroups
        : map(customerPreferences.customAccountGroups, (accountGroup) => {
            return {
              name: accountGroup.groupName,
              accountIds: accountGroup.accountDescriptors.map(
                (accDesc) => accDesc.accountId
              )
            };
          });

    const customGroupOptions = isEmpty(customAccountGroupArray)
      ? []
      : customAccountGroupArray
          .filter(({ accountIds }) =>
            accountIds.some((accountId) => accountIdsLookup[accountId])
          )
          .map(({ name, accountIds }) => ({
            label: name,
            value: `${CUSTOM_ACCOUNT_GROUP_PREFIX}${name}`,
            accountIds
          }));

    transactionsHistoryFilteringSelections.includedAccounts.options =
      concatAndSeparateArrays(
        'divider',
        [allAccountsOption],
        accountOptions,
        predefinedGroups.concat(customGroupOptions) // we don't want a divider between these two
      );
  }

  if (
    !isEmpty(rawTransactionsHistory) &&
    !isHistoricalTransactionFilterSelection
  ) {
    let options = [
      ...transactionsHistoryFilteringSelections.transactionType.options
    ];
    options.splice(1, options.length - 1);

    const transactionsTypeFilterList = uniq(
      rawTransactionsHistory.map((transaction) => transaction.category)
    ).map((listItem) => {
      return {
        label: listItem,
        value: listItem
      };
    });

    if (
      accounts.some(
        (account) =>
          account.productType === ACTIVE_ADVISORY &&
          rawTransactionsHistory.some(
            (transaction) =>
              transaction.accountId === account.accountId &&
              transaction.transactionTypeId === RAS_FEE
          )
      )
    ) {
      transactionsTypeFilterList.push({
        label: RETIREMENT_ADVISORY_FEE_LABEL,
        value: RETIREMENT_ADVISORY_FEE_LABEL
      });
    }

    if (
      accounts.some(
        (account) =>
          rawTransactionsHistory.some(
            (transaction) =>
              transaction.accountId === account.accountId &&
              transaction.associatedWithShareClassConversion === true
          )
      )
    ) {
      transactionsTypeFilterList.push({
        label: SHARE_CLASS_CONVERSION_LABEL,
        value: SHARE_CLASS_CONVERSION_LABEL
      });
    }
    transactionsHistoryFilteringSelections.transactionType.options =
      options.concat(transactionsTypeFilterList);
  }

  if (!isHistoricalTransactionFilterSelection) {
    return transactionsHistoryFilteringSelections;
  }

  const selectionType = camelCase(
    type.replace(SELECT_HISTORICAL_TRANSACTION_PREFIX, '')
  );

  if (selection) {
    switch (selectionType) {
      case TIME_PERIOD:
        transactionsHistoryFilteringSelections[DATE_OPTIONS] = {};
        transactionsHistoryFilteringSelections[selectionType] = {
          selection: selection,
          options: state[selectionType].options
        };
        break;
      case DATE_OPTIONS:
        transactionsHistoryFilteringSelections[selectionType] = {
          selection
        };
        break;
      default:
        transactionsHistoryFilteringSelections[selectionType] = {
          selection: selection,
          options: state[selectionType].options
        };
    }
  }

  return transactionsHistoryFilteringSelections;
};

selectTransactionsHistoryFilteringReducer.priority = 51;

export const transactionsHistoryTableReducer = (
  state = [],
  action,
  {
    accounts = [],
    products,
    rawTransactionsHistory = [],
    transactionsHistoryFilteringSelections = cloneDeep(
      initialTransactionsHistoryFilteringState
    )
  }
) => {
  const { type } = action;
  if (
    (type !== RECEIVED_HISTORICAL_TRANSACTIONS &&
    type !== RECEIVED_ACCOUNTS &&
    !type.startsWith(SELECT_HISTORICAL_TRANSACTION_PREFIX)) ||
    isBrokerageOrderStatusTablesEnabled()
  ) {
    return state;
  }

  const nonPurgedAccounts = filter(accounts, { purgedAccount: false });

  if (!isEmpty(nonPurgedAccounts) && !isEmpty(rawTransactionsHistory)) {
    return buildTransactionsHistoryTable({
      accounts: nonPurgedAccounts,
      providers: products.providers,
      rawTransactionsHistory,
      transactionsHistoryFilteringSelections
    });
  }
  return state;
};

transactionsHistoryTableReducer.priority = 52;

export const selectTransactionsHistoryFilteringReducerV2 = (
  state = cloneDeep(initialTransactionsHistoryFilteringStateV2),
  action,
  {
    rawTransactionsHistory = []
  }
) => {
  const { selection, type } = action;

  const isHistoricalTransactionFilterSelection = type.startsWith(
    SELECT_HISTORICAL_TRANSACTION_PREFIX_V2
  );

  /*
    Ignore if this is not the initial load or there is not a filter selection
   */
  if (
    type !== RECEIVED_HISTORICAL_TRANSACTIONS_V2 &&
    !isHistoricalTransactionFilterSelection
  ) {
    return state;
  }
  const selectionType = camelCase(
    type.replace(SELECT_HISTORICAL_TRANSACTION_PREFIX_V2, '')
  );

  let transactionsHistoryFilteringSelections = cloneDeep(state);

  if (type === RECEIVED_HISTORICAL_TRANSACTIONS_V2 && rawTransactionsHistory) {
    transactionsHistoryFilteringSelections[INCLUDED_ACCOUNTS] = {
      selection: '1',
      options: rawTransactionsHistory.filterCategories.map((filterCategory) => {
        return {
          value: filterCategory?.filterUniqueId,
          label: filterCategory?.filterName,
          agreements: filterCategory?.agreementUniqueIds
        }
      })
    };
    transactionsHistoryFilteringSelections[TRANSACTION_TYPE] = {
      selection: '1',
      options: rawTransactionsHistory.filterCategories.map((filterCategory) => {
        return {
          value: filterCategory?.filterUniqueId,
          label: filterCategory?.filterName,
          agreements: filterCategory?.agreementUniqueIds
        }
      })
    };
  } else if (
    selection
  ) {
    transactionsHistoryFilteringSelections[selectionType] = {
      selection: selection,
      options: state[selectionType].options
    };
  }
  return transactionsHistoryFilteringSelections;
};

selectTransactionsHistoryFilteringReducer.priority = 51;
export const transactionsHistoryTableReducerV2 = (
  state = {},
  action,
  {
    rawTransactionsHistory = []
  }
) => {
  const { uniqueId, type } = action;
  /*
    Ignore any action other than receiving the raw transaction data
  */
  if (
    type !== RECEIVED_HISTORICAL_TRANSACTIONS_V2 &&
    type !== UPDATE_HISTORICAL_TRANSACTION_ACCORDION_ARRAY
  ) {
    return state;
  }

  // TODO: Create a new action for editing the expanded accordions
  // TODO: update the state with the new expanded accordions

  /*
    If we have the raw transaction history table information
    then build the transaction history table
  */
  if (!isUndefined(uniqueId)) {
    const temp = cloneDeep(state.transactionsExpandedAccordion);
    temp[uniqueId] = !state.transactionsExpandedAccordion[uniqueId];
    return {
      ...state,
      transactionsExpandedAccordion: temp
    }
  }
  if (!isEmpty(rawTransactionsHistory)) {
    const {
      historicalTransactions,
      taxonomy
    } = rawTransactionsHistory
    return {
      transactionHistoryAccounts: taxonomy?.agreements,
      transactionsExpandedAccordion: {},
      transactionHistoryData: historicalTransactions.map((transaction, i) => {
        return {
          ...transaction,
          uniqueTransactionHistoryId: `${transaction?.id}-${i}`
        }
      })
    }
  }
  return state;
};

transactionsHistoryTableReducerV2.priority = 52;

export const transactionsPendingTableReducerV2 = (
  state = {},
  action,
  {
    rawPendingTransactionsHistory = {}
  }
) => {
  const { type } = action;
  /*
    Ignore any action other than receiving the raw pending transaction data
  */
  if (
    type !== RECEIVED_PENDING_TRANSACTIONS_V2
  ) {
    return state;
  }

  console.log(rawPendingTransactionsHistory, 'pendingTransactions');
  // TODO: Create a new action for editing the expanded accordions
  // TODO: update the state with the new expanded accordions

  if (rawPendingTransactionsHistory) {
    const {
      details,
      hasAdditionalTransactions,
      pendingTransactions,
      taxonomy
    } = rawPendingTransactionsHistory
    return {
      details: details,
      hasAdditionalTransactions: hasAdditionalTransactions,
      transactionPendingData: pendingTransactions.map((transaction, i) => {
        return {
          ...transaction,
          uniqueTransactionPendingId: `${transaction?.agreementUniqueId}-${i}`
        }
      }),
      pendingTransactionsExpandedAccordion: {},
      transactionPendingAccounts: taxonomy?.agreements,
    }
  }
  return state;
};

transactionsHistoryTableReducerV2.priority = 52;

export const transactionsPendingTableReducer = (
  state = [],
  action,
  { accounts }
) => {
  const { type, transactions } = action;
  if (type !== RECEIVED_PENDING_TRANSACTIONS && type !== RECEIVED_ACCOUNTS) {
    return state;
  }

  const nonPurgedAccounts = filter(accounts, { purgedAccount: false });

  if (!isEmpty(nonPurgedAccounts) && transactions) {
    return buildTransactionsPendingTable({
      accounts: nonPurgedAccounts,
      pendingTransactions: transactions
    });
  }
  return state;
};

export const priorBusinessDayReducer = (state = null, action) => {
  const { type, priorBusinessDay } = action;
  if (type === RECEIVED_PRIOR_BUSINESS_DATE) {
    const { dateValue } = priorBusinessDay;
    return priorBusinessDay && dateValue ? dateValue : null;
  }
  return state;
};
